import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { toast } from 'sonner';
import FileUpload from './FileUpload';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from './ui/select';
import { Receipt, Upload, CheckCircle, Clock, XCircle } from 'lucide-react';

/**
 * ReceiptsPanel — student/parent uploads payment proof; admin can see & approve.
 * Renders inside the Parent Hub section of the Student Dashboard.
 */
export default function ReceiptsPanel({ verticals = [] }) {
  const [pendingFile, setPendingFile] = useState(null);
  const [vertical, setVertical] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('INR');
  const [numClasses, setNumClasses] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [rows, setRows] = useState([]);
  const [showForm, setShowForm] = useState(false);

  const verticalOptions = (verticals || [])
    .map(v => typeof v === 'object' ? v.vertical : v)
    .filter(Boolean);

  useEffect(() => { if (!vertical && verticalOptions.length > 0) setVertical(verticalOptions[0]); }, [verticalOptions, vertical]);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/receipts/mine');
      setRows(res.data.receipts || []);
    } catch (e) { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!pendingFile) return toast.error('Upload the receipt file first');
    if (!vertical || !amount || !numClasses) return toast.error('Fill vertical, amount, and #classes');
    setSubmitting(true);
    try {
      await api.post('/receipts', {
        file_id: pendingFile.id,
        vertical,
        amount: parseFloat(amount),
        currency,
        num_classes: parseInt(numClasses, 10),
        note,
      });
      toast.success('Receipt submitted for admin approval');
      setPendingFile(null); setAmount(''); setNumClasses(''); setNote('');
      setShowForm(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="nb-card p-5" data-testid="receipts-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-[#0F172A] text-sm flex items-center gap-2">
          <Receipt className="w-4 h-4 text-emerald-600" /> Payment Receipts
        </h3>
        <Button size="sm" onClick={() => setShowForm(v => !v)} className="text-xs bg-emerald-600 hover:bg-emerald-700" data-testid="new-receipt-btn">
          <Upload className="w-3 h-3 mr-1" /> {showForm ? 'Cancel' : 'Upload Receipt'}
        </Button>
      </div>

      {showForm && (
        <div className="p-3 rounded-lg border border-emerald-200 bg-emerald-50/40 space-y-3 mb-3" data-testid="receipt-form">
          <FileUpload
            variant="dropzone"
            context="receipt"
            accept="image/*,application/pdf"
            maxSizeMB={25}
            onUploaded={f => setPendingFile(f)}
            label={pendingFile ? `✓ ${pendingFile.filename}` : 'Upload receipt (image or PDF)'}
            dataTestid="receipt-upload"
          />
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-bold text-slate-500 uppercase mb-1 block">Vertical</label>
              <Select value={vertical} onValueChange={setVertical}>
                <SelectTrigger className="h-8 text-xs" data-testid="receipt-vertical"><SelectValue /></SelectTrigger>
                <SelectContent>{verticalOptions.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] font-bold text-slate-500 uppercase mb-1 block">Currency</label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger className="h-8 text-xs" data-testid="receipt-currency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="INR">INR</SelectItem>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="EUR">EUR</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] font-bold text-slate-500 uppercase mb-1 block">Amount</label>
              <Input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="e.g. 3400" className="h-8 text-xs" data-testid="receipt-amount" />
            </div>
            <div>
              <label className="text-[10px] font-bold text-slate-500 uppercase mb-1 block"># Classes</label>
              <Input type="number" value={numClasses} onChange={e => setNumClasses(e.target.value)} placeholder="e.g. 10" className="h-8 text-xs" data-testid="receipt-classes" />
            </div>
          </div>
          <Textarea value={note} onChange={e => setNote(e.target.value)} placeholder="Optional note (bank ref, UPI txn id, etc.)" className="text-xs min-h-[50px]" data-testid="receipt-note" />
          <Button onClick={submit} disabled={submitting || !pendingFile} className="w-full bg-emerald-600 hover:bg-emerald-700 text-xs" data-testid="receipt-submit">
            {submitting ? 'Submitting…' : 'Submit for Approval'}
          </Button>
        </div>
      )}

      {rows.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-3">No receipts uploaded yet.</p>
      ) : (
        <div className="space-y-1.5 max-h-52 overflow-y-auto">
          {rows.map(r => (
            <div key={r.id} className="flex items-center justify-between px-3 py-2 rounded-md bg-slate-50" data-testid={`receipt-row-${r.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold text-slate-800">{r.currency} {r.amount} · {r.vertical} · {r.num_classes} classes</p>
                <p className="text-[10px] text-slate-500">{(r.created_at || '').slice(0, 16).replace('T', ' ')}</p>
              </div>
              {r.status === 'approved' ? (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold flex items-center gap-1"><CheckCircle className="w-3 h-3" /> APPROVED</span>
              ) : r.status === 'rejected' ? (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-bold flex items-center gap-1" title={r.reason || ''}><XCircle className="w-3 h-3" /> REJECTED</span>
              ) : (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-bold flex items-center gap-1"><Clock className="w-3 h-3" /> PENDING</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
